import { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Alert, Tabs, ConfirmDialog } from '@dreamtree-org/twreact-ui';
import { PageHeader, StatusBadge, QueryState } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { useAppApi } from '@/lib/api';
import { errorText } from '@/lib/format';
import OverviewTab from './OverviewTab';
import DomainsTab from './DomainsTab';
import DatabaseTab from './DatabaseTab';
import SubscriptionTab from './SubscriptionTab';
import ProvisioningTab from './ProvisioningTab';
import AuditTab from './AuditTab';
import { usePlatformCan } from '../../lib/access';

/** Tab registry: key (in ?tab=), label, component. */
const TABS = [
  { key: 'overview', label: 'Overview', Panel: OverviewTab },
  { key: 'domains', label: 'Domains', Panel: DomainsTab },
  { key: 'database', label: 'Database & health', Panel: DatabaseTab },
  { key: 'subscription', label: 'Subscription', Panel: SubscriptionTab },
  { key: 'provisioning', label: 'Provisioning', Panel: ProvisioningTab },
  { key: 'audit', label: 'Audit', Panel: AuditTab },
];

/** Lifecycle actions (REAL endpoints), each confirmed first. */
const LIFECYCLE = {
  suspend: { apiKey: 'platformSuspend', label: 'Suspend', when: 'active', destructive: true, describe: (m) => `${m.display_name}'s storefront, admin and service portal stop resolving immediately. You can resume later.` },
  resume: { apiKey: 'platformResume', label: 'Resume', when: 'suspended', describe: (m) => `${m.display_name}'s verified domains start resolving again.` },
  retry: { apiKey: 'platformRetry', label: 'Retry provisioning', when: 'provisioning_failed', describe: () => 'Re-runs the provisioning job from its last completed step. The merchant goes live only if every step succeeds.' },
};

/** One merchant: tabs for identity, domains, isolated DB, subscription, provisioning and audit. */
export default function MerchantDetail() {
  const { id } = useParams();
  const canManage = usePlatformCan('merchants.manage');
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { fetchApi } = useAppApi();
  const merchant = useQuery('platformMerchant', { id }, { select: (r) => r.data });
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const tabIndex = Math.max(0, TABS.findIndex((t) => t.key === params.get('tab')));
  const setTab = (i) => {
    const next = new URLSearchParams(params);
    if (i === 0) next.delete('tab');
    else next.set('tab', TABS[i].key);
    setParams(next, { replace: true });
  };

  /** Run a REAL mutation and reload. Returns the response body. */
  const act = async (key, apiKey, extra = {}) => {
    setBusy(key);
    setErr('');
    setNotice(null);
    try {
      const r = await fetchApi(apiKey, { id, ...extra }, false);
      if (r.data && r.data.admin && r.data.admin.password) setNotice(r.data.admin);
      if (r.data && r.data.ok === false) setErr(`Provisioning stopped: ${r.data.reason || 'see the job below'}`);
      await merchant.reload();
      return r;
    } catch (ex) {
      setErr(errorText(ex));
      return null;
    } finally {
      setBusy('');
    }
  };

  return (
    <QueryState query={merchant}>
      {(m) => {
        const actions = canManage ? Object.entries(LIFECYCLE).filter(([, a]) => a.when === m.status) : [];
        const ctx = { merchant: m, act, busy, reload: merchant.reload, canManage };
        const { Panel } = TABS[tabIndex];
        return (
          <div className="space-y-6">
            <PageHeader
              back={{ label: 'Merchants', href: '/merchants' }}
              title={m.display_name}
              subtitle={<span className="flex flex-wrap items-center gap-2"><StatusBadge status={m.status} /> <code className="text-xs">{m.slug}</code> · {m.city || '—'}</span>}
              actions={actions.map(([key, a]) => (
                <Button key={key} variant={a.destructive ? 'destructive' : key === 'resume' ? 'success' : 'primary'} loading={busy === key} onClick={() => setConfirm(key)}>
                  {a.label}
                </Button>
              ))}
            />

            {err ? <Alert variant="error" title="Action failed" dismissible onDismiss={() => setErr('')}>{err}</Alert> : null}
            {notice ? (
              <Alert variant="warning" title="First admin password — shown once">
                <div className="text-sm space-y-1">
                  <div>{notice.email}</div>
                  <code className="block p-2 rounded bg-[var(--app-surface)] select-all break-all">{notice.password}</code>
                  <div>Share it securely; it is not stored in plaintext and cannot be shown again.</div>
                </div>
              </Alert>
            ) : null}

            <Tabs index={tabIndex} onChange={setTab} orientation="horizontal" variant="line" animated={false}>
              <Tabs.List className="overflow-x-auto whitespace-nowrap border-b border-[var(--app-border)]">
                {TABS.map((t) => <Tabs.Tab key={t.key} className="min-h-[44px]">{t.label}</Tabs.Tab>)}
              </Tabs.List>
              <Tabs.Panels className="pt-4">
                {/* Only the active panel mounts, so each tab loads on demand. */}
                {TABS.map((t, i) => <Tabs.Panel key={t.key}>{i === tabIndex ? <Panel {...ctx} /> : null}</Tabs.Panel>)}
              </Tabs.Panels>
            </Tabs>

            {confirm ? (
              <ConfirmDialog
                isOpen
                title={`${LIFECYCLE[confirm].label} ${m.display_name}?`}
                description={LIFECYCLE[confirm].describe(m)}
                confirmLabel={LIFECYCLE[confirm].label}
                destructive={!!LIFECYCLE[confirm].destructive}
                loading={busy === confirm}
                onCancel={() => setConfirm(null)}
                onConfirm={async () => {
                  await act(confirm, LIFECYCLE[confirm].apiKey);
                  setConfirm(null);
                }}
              />
            ) : null}
          </div>
        );
      }}
    </QueryState>
  );
}
